import { useState, useEffect } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import type {
  BrandSettlementsQuery,
  BrandSettlementsQueryVariables,
} from "../../__generated__/graphql";

// 타입 정의
interface CampaignContract {
  id: number;
  title: string;
}

interface Campaign {
  id: number;
  productName: string;
  channel: string;
  originalPrice: number;
  discountPrice: number;
  paybackPrice: number;
  isPublished: boolean;
  contractId: number | null;
  contract: CampaignContract | null;
  createdAt: string;
}

interface ContractCampaign {
  id: number;
  productName: string;
}

interface SpendCampaign {
  id: number;
  productName: string;
}

interface Spend {
  id: number;
  category: string;
  amount: number;
  description: string;
  occurredAt: string;
  campaign: SpendCampaign | null;
}

interface Contract {
  id: number;
  title: string;
  totalBudget: number;
  startAt: string;
  endAt: string;
  status: string;
  campaigns: ContractCampaign[];
  spends: Spend[];
}

interface Brand {
  id: number;
  name: string;
  roadAddr: string | null;
  likesCount: number;
  campaigns: Campaign[];
  contracts: Contract[];
}

interface CurrentBrandData {
  currentBrand: Brand;
}

const GET_CURRENT_BRAND = gql`
  query CurrentBrand {
    currentBrand {
      id
      name
      roadAddr
      likesCount
      campaigns {
        id
        productName
        channel
        originalPrice
        discountPrice
        paybackPrice
        isPublished
        contractId
        contract {
          id
          title
        }
        createdAt
      }
      contracts {
        id
        title
        totalBudget
        startAt
        endAt
        status
        campaigns {
          id
          productName
        }
        spends {
          id
          category
          amount
          description
          occurredAt
          campaign {
            id
            productName
          }
        }
      }
    }
  }
`;

const GET_BRAND_SETTLEMENTS = gql`
  query BrandSettlements {
    brandSettlements {
      id
      settlementTitle
      finalAmountVatExcluded
      taxInvoiceWrittenAt
      taxInvoice {
        businessName
      }
      brand {
        id
        name
      }
    }
  }
`;

const GET_BRAND_MEMBERS = gql`
  query BrandMembers {
    brandMembers {
      id
      accountId
      role
      account {
        id
        email
        name
        status
        lastLoginAt
        createdAt
      }
      createdAt
    }
  }
`;

const CREATE_BRAND_MEMBER = gql`
  mutation CreateBrandMember($input: CreateBrandMemberInput!) {
    createBrandMember(input: $input) {
      id
      accountId
      role
      account {
        id
        email
        name
        status
      }
      createdAt
    }
  }
`;

const DELETE_BRAND_MEMBER = gql`
  mutation DeleteBrandMember($memberId: Int!) {
    deleteBrandMember(memberId: $memberId) {
      ok
      error
    }
  }
`;

interface BrandMember {
  id: number;
  accountId: number;
  role: string;
  account: {
    id: number;
    email: string;
    name: string | null;
    status: string;
    lastLoginAt: string | null;
    createdAt: string;
  };
  createdAt: string;
}

const BrandDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [settlementsPage, setSettlementsPage] = useState(1);

  // URL 경로에 따라 활성 탭 결정
  const getActiveTab = (): "settlements" | "members" | null => {
    if (location.pathname === "/brand-dashboard/settlements")
      return "settlements";
    if (location.pathname === "/brand-dashboard/members") return "members";
    return null;
  };

  const activeTab = getActiveTab();
  const [memberFormData, setMemberFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "VIEWER",
  });

  const { data, loading, error } =
    useQuery<CurrentBrandData>(GET_CURRENT_BRAND);
  const {
    data: membersData,
    loading: membersLoading,
    refetch: refetchMembers,
  } = useQuery<{ brandMembers: BrandMember[] }>(GET_BRAND_MEMBERS, {
    skip: activeTab !== "members",
  });
  const { data: settlementsData, loading: settlementsLoading } = useQuery<
    BrandSettlementsQuery,
    BrandSettlementsQueryVariables
  >(GET_BRAND_SETTLEMENTS, {
    skip: activeTab !== "settlements",
  });

  // 카운트업 애니메이션을 위한 상태
  const [displayCount, setDisplayCount] = useState(0);
  const targetCount = data?.currentBrand?.likesCount || 0;

  useEffect(() => {
    if (targetCount <= 0) {
      setDisplayCount(0);
      return;
    }

    // 10 이하면 애니메이션 없이 바로 표시
    if (targetCount <= 10) {
      setDisplayCount(targetCount);
      return;
    }

    const duration = 1500; // 애니메이션 지속 시간 (ms)
    const startTime = Date.now();
    const startCount = Math.floor(targetCount / 2); // 목표값의 절반부터 시작

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // easeOutCubic 함수로 부드러운 애니메이션
      const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
      const easedProgress = easeOutCubic(progress);

      const currentCount = Math.floor(
        startCount + (targetCount - startCount) * easedProgress,
      );
      setDisplayCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplayCount(targetCount);
      }
    };

    const animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [targetCount]);

  const [createBrandMember, { loading: creatingMember }] = useMutation(
    CREATE_BRAND_MEMBER,
    {
      onCompleted: () => {
        setIsMemberModalOpen(false);
        setMemberFormData({
          email: "",
          password: "",
          name: "",
          role: "VIEWER",
        });
        refetchMembers();
        alert("멤버가 추가되었습니다.");
      },
      onError: (error) => {
        alert(`멤버 추가 실패: ${error.message}`);
      },
    },
  );

  const [deleteBrandMember, { loading: deletingMember }] = useMutation(
    DELETE_BRAND_MEMBER,
    {
      onCompleted: () => {
        refetchMembers();
        alert("멤버가 삭제되었습니다.");
      },
      onError: (error) => {
        alert(`멤버 삭제 실패: ${error.message}`);
      },
    },
  );

  const handleCreateMember = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!memberFormData.email || !memberFormData.password) {
      alert("아이디와 비밀번호를 입력해주세요.");
      return;
    }

    try {
      await createBrandMember({
        variables: {
          input: {
            email: memberFormData.email.trim(),
            password: memberFormData.password,
            name: memberFormData.name.trim() || null,
            role: memberFormData.role,
          },
        },
      });
    } catch (error) {
      console.error("Member creation error:", error);
    }
  };

  const handleDeleteMember = async (memberId: number, memberEmail: string) => {
    if (!window.confirm(`정말 "${memberEmail}" 멤버를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      await deleteBrandMember({
        variables: { memberId },
      });
    } catch (error) {
      console.error("Member deletion error:", error);
    }
  };

  // 브랜드 주소에서 시/도 정보 추출 (예: "경기도 수원시" -> "수원시")
  const getCityFromAddress = (
    address: string | null | undefined,
  ): string | null => {
    if (!address) return null;

    // 주소에서 시/도 정보 추출
    // 예: "경기도 수원시 영통구" -> "수원시"
    // 예: "서울특별시 강남구" -> "서울특별시"
    const match = address.match(/([가-힣]+(?:시|도))/);
    if (match) {
      return match[1];
    }

    return null;
  };

  const cityName = getCityFromAddress(data?.currentBrand?.roadAddr);

  if (activeTab === null) {
    return (
      <div>
        {/* 통계 카드 */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-red-50">
                <svg
                  className="w-6 h-6 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">
                  {cityName ? `${cityName} 지역 유저` : "좋아요 수"}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {displayCount.toLocaleString()}
                </p>
                {cityName && (
                  <p className="text-xs text-gray-500 mt-1">
                    우리 브랜드 알림을 받아보는 {cityName} 유저수
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-500">에러가 발생했습니다: {error.message}</div>
      </div>
    );
  }

  if (!data?.currentBrand) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">브랜드 정보를 찾을 수 없습니다.</div>
      </div>
    );
  }

  const SETTLEMENTS_PAGE_SIZE = 10;
  const settlementsRows = settlementsData?.brandSettlements || [];
  const settlementsTotal = settlementsRows.length;
  const settlementsTotalPages = Math.max(
    1,
    Math.ceil(settlementsTotal / SETTLEMENTS_PAGE_SIZE),
  );
  const settlementsCurrentPage = Math.min(
    settlementsPage,
    settlementsTotalPages,
  );
  const settlementsStart = (settlementsCurrentPage - 1) * SETTLEMENTS_PAGE_SIZE;
  const settlementsEnd = Math.min(
    settlementsStart + SETTLEMENTS_PAGE_SIZE,
    settlementsTotal,
  );
  const settlementsPageRows = settlementsRows.slice(
    settlementsStart,
    settlementsEnd,
  );

  const settlementsRangeLabel =
    settlementsTotal === 0
      ? "0-0/총 0"
      : `${settlementsStart + 1}-${settlementsEnd}/총 ${settlementsTotal}`;

  const settlementsCanPrev = settlementsCurrentPage > 1;
  const settlementsCanNext = settlementsCurrentPage < settlementsTotalPages;

  const formatSettlementDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  return (
    <div>
      {/* 통계 카드 */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-red-50">
              <svg
                className="w-6 h-6 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm text-gray-600">
                {cityName ? `${cityName} 지역 유저` : "좋아요 수"}
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {displayCount.toLocaleString()}
              </p>
              {cityName && (
                <p className="text-xs text-gray-500 mt-1">
                  우리 브랜드 알림을 받아보는 {cityName} 유저수
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* 탭 컨텐츠 */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        {activeTab === "settlements" && (
          <div>
            {settlementsLoading ? (
              <div className="text-center py-8 text-gray-500">로딩 중...</div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">
                    정산 내역
                  </h2>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">
                      {settlementsRangeLabel}
                    </span>
                    <div className="flex border border-gray-200 rounded overflow-hidden">
                      <button
                        type="button"
                        onClick={() =>
                          setSettlementsPage((p) => Math.max(1, p - 1))
                        }
                        disabled={!settlementsCanPrev}
                        className="px-3 py-1 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label="이전 페이지"
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setSettlementsPage((p) =>
                            Math.min(settlementsTotalPages, p + 1),
                          )
                        }
                        disabled={!settlementsCanNext}
                        className="px-3 py-1 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label="다음 페이지"
                      >
                        ›
                      </button>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          정산명
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          상호명
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          브랜드
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          세금계산서 작성 일자
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          챌린지 정산 금액(vat 별도)
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {settlementsPageRows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-4 py-10 text-center text-gray-500"
                          >
                            정산 내역이 없습니다.
                          </td>
                        </tr>
                      ) : (
                        settlementsPageRows.map((row) => (
                          <tr
                            key={row.id}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() =>
                              navigate(`/brand-dashboard/settlements/${row.id}`)
                            }
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {row.settlementTitle}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-700">
                                {row.taxInvoice?.businessName || "-"}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-700">
                                {row.brand?.name || "-"}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-700">
                                {formatSettlementDate(row.taxInvoiceWrittenAt)}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right">
                              <div className="text-sm font-medium text-gray-900">
                                {parseFloat(
                                  row.finalAmountVatExcluded,
                                ).toLocaleString()}
                                원
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === "members" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">멤버 목록</h2>
              <button
                onClick={() => setIsMemberModalOpen(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                멤버 추가
              </button>
            </div>
            {membersLoading ? (
              <div className="text-center py-8 text-gray-500">로딩 중...</div>
            ) : membersData?.brandMembers &&
              membersData.brandMembers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                등록된 멤버가 없습니다.
              </div>
            ) : (
              <div className="space-y-4">
                {membersData?.brandMembers.map((member) => (
                  <div
                    key={member.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="space-y-2">
                          {member.account.name && (
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-500 w-16">
                                이름:
                              </span>
                              <span className="text-sm text-gray-900">
                                {member.account.name}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-500 w-16">
                              아이디:
                            </span>
                            <span className="text-sm text-gray-900">
                              {member.account.email}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-500 w-16">
                              역할:
                            </span>
                            <span className="text-sm text-gray-900">
                              {member.role}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-500 w-16">
                              상태:
                            </span>
                            <span className="text-sm text-gray-900">
                              {member.account.status === "ACTIVE"
                                ? "활성"
                                : "비활성"}
                            </span>
                          </div>
                          {member.account.lastLoginAt && (
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-500 w-16">
                                최종 로그인:
                              </span>
                              <span className="text-sm text-gray-900">
                                {new Date(
                                  member.account.lastLoginAt,
                                ).toLocaleString()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() =>
                            handleDeleteMember(member.id, member.account.email)
                          }
                          disabled={deletingMember}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 멤버 추가 모달 */}
      {isMemberModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">멤버 추가</h2>
            <form onSubmit={handleCreateMember}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    아이디 *
                  </label>
                  <input
                    type="text"
                    required
                    value={memberFormData.email}
                    onChange={(e) =>
                      setMemberFormData({
                        ...memberFormData,
                        email: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="아이디를 입력하세요"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    비밀번호 *
                  </label>
                  <input
                    type="password"
                    required
                    value={memberFormData.password}
                    onChange={(e) =>
                      setMemberFormData({
                        ...memberFormData,
                        password: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="비밀번호를 입력하세요"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    이름 (선택)
                  </label>
                  <input
                    type="text"
                    value={memberFormData.name}
                    onChange={(e) =>
                      setMemberFormData({
                        ...memberFormData,
                        name: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="이름을 입력하세요"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    역할 *
                  </label>
                  <select
                    value={memberFormData.role}
                    onChange={(e) =>
                      setMemberFormData({
                        ...memberFormData,
                        role: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="ADMIN">ADMIN</option>
                    <option value="EDITOR">EDITOR</option>
                    <option value="VIEWER">VIEWER</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setIsMemberModalOpen(false);
                    setMemberFormData({
                      email: "",
                      password: "",
                      name: "",
                      role: "VIEWER",
                    });
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={creatingMember}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {creatingMember ? "추가 중..." : "추가"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandDashboard;
